export interface SignatureStyle {
    id: string;
    label: string;
    /** CSS font-family stack; the primary family is loaded via @fontsource. */
    fontFamily: string;
}

export const SIGNATURE_STYLES: SignatureStyle[] = [
    { id: 'dancing', label: 'Dancing Script', fontFamily: "'Dancing Script', cursive" },
    { id: 'great-vibes', label: 'Great Vibes', fontFamily: "'Great Vibes', cursive" },
    { id: 'sacramento', label: 'Sacramento', fontFamily: "'Sacramento', cursive" },
];

/**
 * First + last word initial (uppercased); a single initial for one-word names.
 * Used to pre-fill the initials field, DocuSign-style.
 */
export const deriveInitials = (fullName: string): string => {
    const words = fullName.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Rasterize text in a cursive style onto a transparent canvas and return a PNG
 * data URL. Waits for the font to load so the first render isn't a fallback.
 */
export const renderTextSignatureToPng = async (
    text: string,
    fontFamily: string,
    options: { width?: number; height?: number; fontSize?: number } = {},
): Promise<string> => {
    const width = options.width ?? 600;
    const height = options.height ?? 200;
    const fontSize = options.fontSize ?? 96;

    const font = `${fontSize}px ${fontFamily}`;
    try {
        if (typeof document !== 'undefined' && document.fonts?.load)
            await document.fonts.load(font, text || 'A');
    } catch {
        // Fall back to whatever the browser has; rasterization still succeeds.
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#1a1a6e';
        ctx.font = font;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, width / 2, height / 2);
    }
    return canvas.toDataURL('image/png');
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image load failed'));
        img.src = src;
    });

/**
 * Crop the fully-transparent margin around a PNG so a drawn/generated signature
 * fills the field box it is later stamped into (instead of floating tiny in a
 * large transparent canvas). Returns the original if there is nothing opaque.
 */
export const trimTransparentPng = async (
    dataUrl: string,
    padding = 6,
): Promise<{ dataUrl: string; width: number; height: number }> => {
    const img = await loadImage(dataUrl);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { dataUrl, width: w, height: h };
    ctx.drawImage(img, 0, 0);

    const { data } = ctx.getImageData(0, 0, w, h);
    let minX = w;
    let minY = h;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
            if (data[(y * w + x) * 4 + 3] !== 0) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }

    if (maxX < 0) return { dataUrl, width: w, height: h };

    const left = Math.max(0, minX - padding);
    const top = Math.max(0, minY - padding);
    const right = Math.min(w, maxX + padding + 1);
    const bottom = Math.min(h, maxY + padding + 1);
    const cropW = right - left;
    const cropH = bottom - top;

    const out = document.createElement('canvas');
    out.width = cropW;
    out.height = cropH;
    const outCtx = out.getContext('2d');
    if (!outCtx) return { dataUrl, width: w, height: h };
    outCtx.drawImage(img, left, top, cropW, cropH, 0, 0, cropW, cropH);
    return { dataUrl: out.toDataURL('image/png'), width: cropW, height: cropH };
};
